import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail, IMAGES } from "@/lib/site";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ImageUploader from "@/components/ImageUploader";
import { toast } from "sonner";
import { LogOut, Users, MessageSquare, Star, Images, Trash2, Plus, ExternalLink, Megaphone, Bell, Calendar, Award } from "lucide-react";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color}`}><Icon size={20} /></div>
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-widest">{label}</p>
          <p className="font-serif-display text-3xl font-bold text-royal">{value}</p>
        </div>
      </div>
    </div>
  );
}

const NOTICE_CATEGORIES = [
  { value: "announcement", label: "Announcement", icon: Megaphone },
  { value: "event", label: "Event / Function", icon: Calendar },
  { value: "result", label: "Exam Result", icon: Award },
  { value: "general", label: "General", icon: Bell },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [admissions, setAdmissions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [notices, setNotices] = useState([]);
  const [newReview, setNewReview] = useState({ name: "", role: "Parent", rating: 5, message: "" });
  const [newGallery, setNewGallery] = useState({ title: "", image_url: "", category: "general" });
  const [newNotice, setNewNotice] = useState({ title: "", body: "", category: "announcement", image_url: "" });

  const loadAll = async () => {
    try {
      const [a, c, r, g, n] = await Promise.all([
        api.get("/admin/admissions"),
        api.get("/admin/contacts"),
        api.get("/admin/reviews"),
        api.get("/admin/gallery"),
        api.get("/admin/notices"),
      ]);
      setAdmissions(a.data); setContacts(c.data); setReviews(r.data); setGallery(g.data); setNotices(n.data);
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "admin") {
      navigate("/admin/login");
      return;
    }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const del = async (kind, id) => {
    if (!window.confirm("Delete this entry?")) return;
    try {
      await api.delete(`/admin/${kind}/${id}`);
      toast.success("Deleted");
      loadAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  const addReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/reviews", newReview);
      toast.success("Review added");
      setNewReview({ name: "", role: "Parent", rating: 5, message: "" });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const addGallery = async (e) => {
    e.preventDefault();
    if (!newGallery.image_url) return toast.error("Please upload an image first");
    try {
      await api.post("/admin/gallery", newGallery);
      toast.success("Image added to gallery");
      setNewGallery({ title: "", image_url: "", category: "general" });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const addNotice = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/notices", newNotice);
      toast.success("Notice published");
      setNewNotice({ title: "", body: "", category: "announcement", image_url: "" });
      loadAll();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5]"><p className="text-royal">Loading...</p></div>;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#f5f5f5]">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-black ring-2 ring-gold/60 overflow-hidden flex items-center justify-center">
              <img src={IMAGES.logo} alt="Royal English logo" className="w-full h-full object-contain p-0.5" />
            </div>
            <div>
              <p className="font-serif-display font-bold text-royal leading-tight">Admin Dashboard</p>
              <p className="text-[10px] text-gold tracking-widest">ROYAL ENGLISH MEDIUM HIGH SCHOOL</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="view-site" className="text-royal hover:text-gold inline-flex items-center gap-1 text-sm font-semibold">
              <ExternalLink size={14} /> View Site
            </Link>
            <Button data-testid="logout-btn" onClick={handleLogout} variant="outline" className="rounded-full">
              <LogOut size={16} className="mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <StatCard icon={Users} label="Admissions" value={admissions.length} color="bg-royal/10 text-royal" />
          <StatCard icon={MessageSquare} label="Messages" value={contacts.length} color="bg-gold/15 text-gold-dark" />
          <StatCard icon={Megaphone} label="Notices" value={notices.length} color="bg-amber-100 text-amber-700" />
          <StatCard icon={Star} label="Reviews" value={reviews.length} color="bg-green-100 text-green-700" />
          <StatCard icon={Images} label="Gallery" value={gallery.length} color="bg-blue-100 text-blue-700" />
        </div>

        <Tabs defaultValue="admissions" className="bg-white rounded-3xl p-6 border border-gray-100">
          <TabsList className="grid w-full md:w-auto grid-cols-5 mb-6">
            <TabsTrigger value="admissions" data-testid="tab-admissions">Admissions</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Messages</TabsTrigger>
            <TabsTrigger value="notices" data-testid="tab-notices">Notices</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
          </TabsList>

          <TabsContent value="admissions">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-gray-500 uppercase text-xs tracking-wider border-b">
                  <tr><th className="py-3">Date</th><th>Student</th><th>Parent</th><th>Class</th><th>Phone</th><th>Email</th><th></th></tr>
                </thead>
                <tbody>
                  {admissions.map((a) => (
                    <tr key={a.id} className="border-b border-gray-100" data-testid={`admission-row-${a.id}`}>
                      <td className="py-3 text-gray-500">{new Date(a.created_at).toLocaleDateString()}</td>
                      <td className="font-semibold text-royal">{a.student_name}</td>
                      <td>{a.parent_name}</td>
                      <td>{a.student_class}</td>
                      <td><a href={`tel:${a.phone}`} className="text-royal hover:text-gold">{a.phone}</a></td>
                      <td className="text-gray-600">{a.email || "-"}</td>
                      <td><button onClick={() => del("admissions", a.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                  {admissions.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-gray-400">No admission enquiries yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} data-testid={`contact-row-${c.id}`} className="bg-[#f7f7f7] rounded-2xl p-5 border border-gray-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-royal">{c.name} <span className="text-gray-400 text-xs font-normal">• {new Date(c.created_at).toLocaleString()}</span></p>
                      <p className="text-sm text-gray-600">{c.email} {c.phone && `• ${c.phone}`}</p>
                      {c.subject && <p className="text-sm font-semibold mt-1">Subject: {c.subject}</p>}
                    </div>
                    <button onClick={() => del("contacts", c.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                  </div>
                  <p className="mt-3 text-gray-700 whitespace-pre-wrap">{c.message}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="py-10 text-center text-gray-400">No messages yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="notices">
            <form onSubmit={addNotice} className="bg-[#f7f7f7] rounded-2xl p-6 mb-6 grid md:grid-cols-2 gap-4 border border-gray-100" data-testid="add-notice-form">
              <div>
                <Label className="font-semibold">Title *</Label>
                <Input data-testid="new-notice-title" required placeholder="e.g., Annual Sports Day Results" value={newNotice.title} onChange={(e) => setNewNotice({ ...newNotice, title: e.target.value })} className="h-11 bg-white mt-1.5" />
              </div>
              <div>
                <Label className="font-semibold">Category *</Label>
                <Select value={newNotice.category} onValueChange={(v) => setNewNotice({ ...newNotice, category: v })}>
                  <SelectTrigger data-testid="new-notice-category" className="h-11 bg-white mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {NOTICE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="font-semibold">Content *</Label>
                <Textarea data-testid="new-notice-body" required rows={5} placeholder="Write the full notice / announcement here..." value={newNotice.body} onChange={(e) => setNewNotice({ ...newNotice, body: e.target.value })} className="bg-white mt-1.5" />
              </div>
              <div className="md:col-span-2 flex items-center gap-4 flex-wrap">
                <ImageUploader folder="uploads/notices" label="Attach Image (optional)" onUploaded={(url) => setNewNotice({ ...newNotice, image_url: url })} />
                {newNotice.image_url && <img src={newNotice.image_url} alt="preview" className="h-14 w-14 object-cover rounded-lg border" />}
                <Button type="submit" data-testid="publish-notice" className="ml-auto bg-royal text-white rounded-full px-6"><Plus size={16} className="mr-1" />Publish Notice</Button>
              </div>
            </form>

            <div className="grid md:grid-cols-2 gap-4">
              {notices.map((n) => (
                <div key={n.id} data-testid={`notice-row-${n.id}`} className="bg-[#f7f7f7] rounded-2xl p-5 border border-gray-100">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-[11px] uppercase tracking-widest font-bold text-gold-dark">{n.category}</p>
                      <p className="font-bold text-royal mt-1">{n.title}</p>
                      <p className="text-xs text-gray-500">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={() => del("notices", n.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                  </div>
                  {n.image_url && <img src={n.image_url} alt={n.title} className="mt-3 w-full h-32 object-cover rounded-xl" />}
                  <p className="mt-3 text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">{n.body}</p>
                </div>
              ))}
              {notices.length === 0 && <p className="py-10 text-center text-gray-400 col-span-2">No notices yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="reviews">
            <form onSubmit={addReview} className="bg-[#f7f7f7] rounded-2xl p-5 mb-6 grid md:grid-cols-4 gap-3 border border-gray-100" data-testid="add-review-form">
              <Input data-testid="new-review-name" placeholder="Name" required value={newReview.name} onChange={(e) => setNewReview({ ...newReview, name: e.target.value })} className="h-10 bg-white" />
              <Input placeholder="Role (Parent/Student)" value={newReview.role} onChange={(e) => setNewReview({ ...newReview, role: e.target.value })} className="h-10 bg-white" />
              <Input type="number" min={1} max={5} placeholder="Rating" value={newReview.rating} onChange={(e) => setNewReview({ ...newReview, rating: Number(e.target.value) })} className="h-10 bg-white" />
              <Button type="submit" className="bg-royal text-white rounded-full"><Plus size={16} className="mr-1" />Add</Button>
              <Textarea data-testid="new-review-message" placeholder="Review message" required value={newReview.message} onChange={(e) => setNewReview({ ...newReview, message: e.target.value })} className="md:col-span-4 bg-white" />
            </form>
            <div className="grid md:grid-cols-2 gap-3">
              {reviews.map((r) => (
                <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-[#f7f7f7] rounded-2xl p-5 border border-gray-100">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-bold text-royal">{r.name}</p>
                      <p className="text-xs text-gray-500">{r.role} • {r.rating}★</p>
                    </div>
                    <button onClick={() => del("reviews", r.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                  </div>
                  <p className="mt-3 text-sm text-gray-700">{r.message}</p>
                </div>
              ))}
              {reviews.length === 0 && <p className="py-10 text-center text-gray-400 col-span-2">No reviews yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="gallery">
            <form onSubmit={addGallery} className="bg-[#f7f7f7] rounded-2xl p-6 mb-6 border border-gray-100 grid md:grid-cols-12 gap-4 items-start" data-testid="add-gallery-form">
              <div className="md:col-span-5">
                <Label className="font-semibold">Image Title *</Label>
                <Input data-testid="new-gallery-title" placeholder="e.g., Sports Day 2025" required value={newGallery.title} onChange={(e) => setNewGallery({ ...newGallery, title: e.target.value })} className="h-11 bg-white mt-1.5" />
              </div>
              <div className="md:col-span-3">
                <Label className="font-semibold">Category</Label>
                <Input placeholder="general / events / sports" value={newGallery.category} onChange={(e) => setNewGallery({ ...newGallery, category: e.target.value })} className="h-11 bg-white mt-1.5" />
              </div>
              <div className="md:col-span-4 flex flex-col gap-2">
                <Label className="font-semibold">Upload Image *</Label>
                <div className="flex items-center gap-3">
                  <ImageUploader folder="uploads/gallery" onUploaded={(url) => setNewGallery({ ...newGallery, image_url: url })} />
                  {newGallery.image_url && <img src={newGallery.image_url} alt="preview" className="h-11 w-11 object-cover rounded-lg border" />}
                </div>
              </div>
              <div className="md:col-span-12 flex justify-end">
                <Button type="submit" data-testid="save-gallery-item" className="bg-royal text-white rounded-full px-6"><Plus size={16} className="mr-1" />Add to Gallery</Button>
              </div>
            </form>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {gallery.map((g) => (
                <div key={g.id} data-testid={`gallery-row-${g.id}`} className="relative group rounded-2xl overflow-hidden shadow-md">
                  <img src={g.image_url} alt={g.title} className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-royal/80 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center text-white p-3 transition-opacity">
                    <p className="text-sm font-semibold text-center">{g.title}</p>
                    <button onClick={() => del("gallery", g.id)} className="mt-3 bg-red-500 hover:bg-red-600 px-3 py-1 rounded-full text-xs"><Trash2 size={12} className="inline mr-1" />Delete</button>
                  </div>
                </div>
              ))}
              {gallery.length === 0 && <p className="py-10 text-center text-gray-400 col-span-full">No images yet.</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
